import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { API } from '@/api'
import { message, Modal, Trans } from '@/components'
import { Instance } from '@/models/instance'

interface CancelSnapshotOptions {
  onSuccess?: () => void
}

export function useCancelSnapshot(options: CancelSnapshotOptions = {}) {
  const { t } = useTranslation()
  const { onSuccess } = options

  const cancelSnapshot = useCallback(
    async (snapshotId: number) => {
      await Modal.confirm({
        title: t('instance.cancelSnapshotModal.title', 'Cancel Snapshot'),
        content: (
          <div className="bg-panel-gray-100 rounded-lg p-4 flex-row">
            <div className="t-sm font-medium text-secondary">
              <Trans i18nKey="instance.cancelSnapshotModal.content">
                Are you sure you want to cancel this snapshot operation? The snapshot will not be
                saved.
              </Trans>
            </div>
          </div>
        ),
        confirmBtnCfg: {
          type: 'danger',
          text: t('instance.cancelSnapshotModal.confirmBtn', 'Cancel Snapshot'),
        },
        className: 'w-[640px]',
      })

      const res = await API.cancelSnapshot({ id: snapshotId, cancel_snapshot: true })
      if (res.code === 0) {
        message.success(t('instance.cancelSnapshotModal.successMsg', 'Snapshot cancelled'))
        onSuccess?.()
      }
    },
    [t, onSuccess],
  )

  // Wrapper for Instance objects
  const cancelInstanceSnapshot = useCallback(
    async (instance: Instance) => {
      const snapshot = instance.currentSavingSnapshot
      if (!snapshot) return
      await cancelSnapshot(snapshot.id)
    },
    [cancelSnapshot],
  )

  return { cancelSnapshot, cancelInstanceSnapshot }
}
